// Workflow choice above the platform-format picker: standard ads (tick the
// surfaces you want) or a scene reel (one fixed 10 s cut per product, three
// aspects). Rendered by the wizard ONLY when GET /api/ads/formats lists an
// enabled scene-reel workflow and the campaign is not brand-kind, so with the
// backend flag off the wizard looks exactly as it did before.
//
// Copy rules (ui-smoke journeys.js scans the whole page): never "video
// generation", "image generation", "master" or "size", and no button whose
// first line is "All video" / "All static".

import { Box, Flex, HStack, Text, Badge } from '@chakra-ui/react';
import type { WizardWorkflow } from './index';
import type { WorkflowEntry } from './formatCatalog';
import { cutLabel } from '../../lib/sceneReel';

export function WorkflowPicker({
  value,
  workflow,
  onChange
}: {
  value:    WizardWorkflow;
  workflow: WorkflowEntry;
  onChange: (next: WizardWorkflow) => void;
}) {
  const derivedAspects = workflow.outputs.filter(o => o.derived).map(o => o.aspect);
  const generatedAspects = workflow.outputs.filter(o => !o.derived).map(o => o.aspect);
  const sceneSubtitle = [
    generatedAspects.length ? `${generatedAspects.join(' + ')} generated` : null,
    derivedAspects.length ? `${derivedAspects.join(' + ')} cropped` : null,
    'overlays from product data'
  ].filter(Boolean).join(' · ');
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" bg="brand.surface" p={4}>
      <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={3}>
        Workflow
      </Text>
      <Flex gap={2} wrap="wrap" role="radiogroup" aria-label="Workflow">
        <WorkflowCard
          title="Standard ads"
          subtitle="Static and video ads for the platform formats you pick below"
          on={value === 'standard'}
          onSelect={() => onChange('standard')}
        />
        <WorkflowCard
          title={`${workflow.label} — ${workflow.durationSec} s, ${workflow.cut.length} scenes`}
          subtitle={sceneSubtitle}
          badge={cutLabel(workflow.cut)}
          on={value === 'scene_reel'}
          onSelect={() => onChange('scene_reel')}
        />
      </Flex>
    </Box>
  );
}

function WorkflowCard({
  title, subtitle, badge, on, onSelect
}: {
  title:    string;
  subtitle: string;
  badge?:   string;
  on:       boolean;
  onSelect: () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onSelect}
      flex="1 1 240px"
      textAlign="left"
      px={3}
      py={2}
      borderWidth="1px"
      borderColor={on ? 'rsViolet.500' : 'brand.border'}
      bg={on ? 'rsViolet.500' : 'white'}
      color={on ? 'white' : 'brand.ink'}
      borderRadius="md"
      cursor="pointer"
      transition="border-color 0.12s, background 0.12s"
    >
      <HStack spacing={2} justify="space-between" align="baseline">
        <Text fontSize="12px" fontWeight="800">{title}</Text>
        {badge && (
          <Badge fontSize="9px" colorScheme={on ? 'whiteAlpha' : 'purple'} variant="subtle" flex="none">
            {badge}
          </Badge>
        )}
      </HStack>
      <Text fontSize="10px" mt={0.5} opacity={on ? 0.9 : 0.7} color={on ? 'white' : 'brand.muted'}>
        {subtitle}
      </Text>
    </Box>
  );
}
